/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import "moment/locale/th";

// FullCalendar components & plugins
import Fullcalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import Chip from "@material-ui/core/Chip";
import Avatar from "@material-ui/core/Avatar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Badge from "@material-ui/core/Badge";
import Paper from "@material-ui/core/Paper";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";

// @material-ui/icons
import VerifiedUser from "@material-ui/icons/VerifiedUser";
import AccessTime from "@material-ui/icons/AccessTime";
import FlightTakeoff from "@material-ui/icons/FlightTakeoff";
import Assignment from "@material-ui/icons/Assignment";
import Today from "@material-ui/icons/Today";
import CheckCircle from "@material-ui/icons/CheckCircle";
import Close from "@material-ui/icons/Close";
import Person from "@material-ui/icons/Person";
import EventNote from "@material-ui/icons/EventNote";

// Core components
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "components/Card/CardFooter.js";
import Button from "components/CustomButtons/Button.js";
import TSSelect2 from "components/Forms/TSSelect2";

const useStyles = makeStyles((theme) => ({
  cardCategoryWhite: {
    color: "rgba(255,255,255,.8)",
    margin: "0",
    fontSize: "14px",
    marginTop: "0",
    marginBottom: "0",
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "500",
    fontFamily: '"Sarabun", sans-serif',
    marginBottom: "3px",
    textDecoration: "none",
  },
  statsCategory: {
    color: "#777",
    margin: "0",
    fontSize: "13px",
    fontWeight: "bold",
  },
  statsTitle: {
    color: "#3C4858",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "bold",
    fontFamily: '"Sarabun", sans-serif',
    fontSize: "26px",
    marginBottom: "3px",
  },
  statsUnit: {
    fontSize: "16px",
    fontWeight: "normal",
    color: "#888",
    marginLeft: "4px",
  },
  tableResponsive: {
    width: "100%",
    overflowX: "auto",
  },
  calendarCard: {
    "& .fc": {
      fontFamily: '"Sarabun", sans-serif',
    },
    "& .fc-toolbar-title": {
      fontSize: "1.25rem !important",
      fontWeight: "bold",
      color: "#333",
    },
    "& .fc-button-primary": {
      backgroundColor: "#1976d2 !important",
      borderColor: "#1976d2 !important",
      textTransform: "capitalize",
      fontWeight: "bold",
      boxShadow: "none !important",
      "&:hover": {
        backgroundColor: "#1565c0 !important",
      },
    },
    "& .fc-button-active": {
      backgroundColor: "#0d47a1 !important",
      borderColor: "#0d47a1 !important",
    },
    "& .fc-event": {
      borderRadius: "4px",
      padding: "2px 5px",
      fontSize: "12px",
      cursor: "pointer",
      border: "none",
      boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
      transition: "transform 0.15s ease",
      "&:hover": {
        transform: "scale(1.02)",
        filter: "brightness(0.95)",
      },
    },
    "& .fc-day-today": {
      backgroundColor: "rgba(25, 118, 210, 0.06) !important",
    },
    "& .fc-col-header-cell": {
      backgroundColor: "#f5f7fa",
      padding: "8px 0",
      fontWeight: "bold",
      color: "#555",
    },
  },
  legendContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: "#fafafa",
    borderRadius: "6px",
    marginBottom: "16px",
    alignItems: "center",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12.5px",
    fontWeight: "500",
    color: "#444",
  },
  legendDot: {
    width: "12px",
    height: "12px",
    borderRadius: "3px",
    display: "inline-block",
  },
  tabCustom: {
    fontWeight: "bold",
    fontFamily: '"Sarabun", sans-serif',
    fontSize: "14px",
  },
}));

const MyApprove = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const basePath = location.pathname.startsWith("/leave") ? "/leave/attendance" : "/hr/attendance";
  const calendarRef = useRef(null);

  // คำนวณปีงบประมาณปัจจุบัน
  let currentThaiYear = parseInt(moment().format("BBBB"));
  if (isNaN(currentThaiYear)) {
    currentThaiYear = parseInt(moment().format("YYYY")) + 543;
  }
  const currentFiscalYear = moment().month() >= 9 ? currentThaiYear + 1 : currentThaiYear;

  const [fiscalYear, setFiscalYear] = useState(currentFiscalYear);
  const [tabIndex, setTabIndex] = useState(0); // 0: ทั้งหมด, 1: กจ. 1/กจ. 4, 2: การลา
  const [loading, setLoading] = useState(false);

  // ข้อมูลคำขอรออนุมัติ
  const [attendanceList, setAttendanceList] = useState([]);
  const [leaveList, setLeaveList] = useState([]);

  // Modal แสดงรายละเอียด Event ที่ถูกคลิก
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // ตัวเลือกปีงบประมาณ
  const fiscalYearOptions = [
    { value: currentFiscalYear + 1, label: `ปีงบประมาณ ${currentFiscalYear + 1}` },
    { value: currentFiscalYear, label: `ปีงบประมาณ ${currentFiscalYear}` },
    { value: currentFiscalYear - 1, label: `ปีงบประมาณ ${currentFiscalYear - 1}` },
    { value: currentFiscalYear - 2, label: `ปีงบประมาณ ${currentFiscalYear - 2}` },
    { value: currentFiscalYear - 3, label: `ปีงบประมาณ ${currentFiscalYear - 3}` },
  ];

  // ดึงข้อมูลรายการรออนุมัติทั้ง 2 ระบบ
  const fetchApprovals = () => {
    setLoading(true);

    // 1. ดึงรายการขอลงเวลา กจ. 1 / กจ. 4
    const fetchAttendance = axios
      .get("/api/v1/timeattendance/myapprove")
      .then((res) => {
        if (res.data?.data) {
          setAttendanceList(res.data.data);
        } else {
          setAttendanceList([]);
        }
      })
      .catch((err) => {
        console.log(err);
        setAttendanceList([]);
      });

    // 2. ดึงรายการขออนุมัติลา (ประจำปีงบประมาณ)
    const fetchLeave = axios
      .get(`/api/v1/leave/myapprove/?Calyear=${fiscalYear}&ApproveStatusKey=1`)
      .then((res) => {
        if (Array.isArray(res.data)) {
          setLeaveList(res.data);
        } else if (res.data?.data && Array.isArray(res.data.data)) {
          setLeaveList(res.data.data);
        } else {
          setLeaveList([]);
        }
      })
      .catch((err) => {
        console.log(err);
        setLeaveList([]);
      });

    Promise.all([fetchAttendance, fetchLeave]).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchApprovals();
  }, [fiscalYear]);

  // =========================================================
  // แปลงข้อมูลเป็น Events บนปฏิทิน
  // =========================================================
  const calendarEvents = [];

  // 1. Events ขออนุมัติลา (แถบยาวหลายวัน)
  leaveList.forEach((l) => {
    let typeName = "ขอลา";
    let bgColor = "#43a047"; // เขียว (พักผ่อน)

    if (l.leaveTypeKey === 1) {
      typeName = "ลาป่วย";
      bgColor = "#e53935"; // แดง
    } else if (l.leaveTypeKey === 2) {
      typeName = "ลากิจ";
      bgColor = "#fb8c00"; // ส้ม
    } else if (l.leaveTypeKey === 3) {
      typeName = "ลาพักผ่อน";
      bgColor = "#43a047"; // เขียว
    } else if (l.leaveTypeName) {
      typeName = l.leaveTypeName;
      bgColor = "#5c6bc0";
    }

    const startDateStr = moment(l.startDate || l.start).format("YYYY-MM-DD");
    const endDateRaw = l.endDate || l.end || l.startDate || l.start;
    const endDateStr = moment(endDateRaw).add(1, "days").format("YYYY-MM-DD");

    const requester = l.userView
      ? `${l.userView.firstName} ${l.userView.lastName}`
      : l.fullName || l.userName || "ผู้ขอลา";
    const titleText = `[${typeName}] ${requester} (${l.totalDate || 1} วัน)`;

    calendarEvents.push({
      id: `leave_${l.leaveRequestID || l.id}`,
      title: titleText,
      start: startDateStr,
      end: endDateStr,
      allDay: true,
      backgroundColor: bgColor,
      borderColor: bgColor,
      textColor: "#ffffff",
      extendedProps: {
        category: "leave",
        raw: l,
        leaveRequestID: l.leaveRequestID || l.id,
        requesterName: requester,
        userName: l.userName,
        typeName: typeName,
        totalDate: l.totalDate,
        startDate: l.startDate || l.start,
        endDate: endDateRaw,
        reason: l.leaveDesc || l.title || "",
      },
    });
  });

  // 2. Events ขอลงเวลาปฏิบัติราชการ (วันละ 1 แท่ง)
  attendanceList.forEach((a) => {
    let typeName = a.categoryName || "ลงเวลา";
    let bgColor = "#0288d1"; // ฟ้า

    if (a.categoryMode === 10 || a.attendanceType === 10) {
      typeName = a.categoryName || "ขอตามโควต้า";
      bgColor = "#2e7d32"; // เขียว
    } else if (a.attendanceType === 1) {
      typeName = a.categoryName || "กจ. 1 (ลืมเข้า)";
      bgColor = "#0288d1";
    } else if (a.attendanceType === 2) {
      typeName = a.categoryName || "กจ. 4 (ลืมออก)";
      bgColor = "#8e24aa";
    } else if (a.attendanceType === 3) {
      typeName = a.categoryName || "กจ. 1/4 (ลืมเข้า-ออก)";
      bgColor = "#d81b60";
    }

    const targetDate = a.workDate || a.checkInDT || a.checkOutDT;
    const dateStr = moment(targetDate).format("YYYY-MM-DD");
    const requester = a.requesterName || a.userName || "ผู้ขอลงเวลา";
    const titleText = `[${typeName}] ${requester}`;

    calendarEvents.push({
      id: `att_${a.timeAttendanceRequestID}`,
      title: titleText,
      start: dateStr,
      end: dateStr,
      allDay: true,
      backgroundColor: bgColor,
      borderColor: bgColor,
      textColor: "#ffffff",
      extendedProps: {
        category: "attendance",
        raw: a,
        timeAttendanceRequestID: a.timeAttendanceRequestID,
        requesterName: requester,
        userName: a.userName,
        typeName: typeName,
        attendanceType: a.attendanceType,
        workDate: targetDate,
        reason: a.reason,
        checkInDT: a.checkInDT,
        checkOutDT: a.checkOutDT,
        stepOrder: a.approveStep?.stepOrder,
      },
    });
  });

  // จัดการเมื่อคลิก Event บนปฏิทิน
  const handleEventClick = (info) => {
    const props = info.event.extendedProps;
    if (props.category === "attendance" && props.timeAttendanceRequestID) {
      history.push(`${basePath}/request/${props.timeAttendanceRequestID}`);
    } else if (props.category === "leave" && props.leaveRequestID) {
      history.push({
        pathname: `/leave/id/${props.leaveRequestID}`,
        state: { leaveRequestID: props.leaveRequestID },
      });
    } else {
      setSelectedEvent({
        title: info.event.title,
        ...props,
      });
      setModalOpen(true);
    }
  };

  const getAttendanceTypeText = (rowOrType) => {
    if (typeof rowOrType === "object" && rowOrType !== null) {
      if (rowOrType.categoryName) {
        return rowOrType.durationHours ? `${rowOrType.categoryName} (${rowOrType.durationHours} ชม.)` : rowOrType.categoryName;
      }
      const type = rowOrType.attendanceType;
      if (type === 1) return "ลงเวลาเข้า (กจ. 1)";
      if (type === 2) return "ลงเวลาออก (กจ. 4)";
      if (type === 3) return "ทั้งเข้าและออก (กจ. 1 / กจ. 4)";
      if (type === 10) return "ขอตามโควต้า";
      return "-";
    }
    const type = rowOrType;
    if (type === 1) return "ลงเวลาเข้า (กจ. 1)";
    if (type === 2) return "ลงเวลาออก (กจ. 4)";
    if (type === 3) return "ทั้งเข้าและออก (กจ. 1 / กจ. 4)";
    if (type === 10) return "ขอตามโควต้า";
    return "-";
  };

  const getRoleBadge = (stepOrder, row) => {
    const isDisableLv1 = Boolean(row?.category?.isDisableLv1);
    if (isDisableLv1) {
      if (stepOrder === 1) return <Chip label="ผู้บังคับบัญชา 1 ระดับ" color="primary" size="small" />;
      if (stepOrder === 2) return <Chip label="งานทรัพยากรบุคคล" style={{ backgroundColor: "#e91e63", color: "#fff" }} size="small" />;
    } else {
      if (stepOrder === 1) return <Chip label={row?.category?.lv1Text || "พยานร่วมรับรอง"} color="secondary" size="small" />;
      if (stepOrder === 2) return <Chip label="ผู้บังคับบัญชา 1 ระดับ" color="primary" size="small" />;
      if (stepOrder === 3) return <Chip label="งานทรัพยากรบุคคล" style={{ backgroundColor: "#e91e63", color: "#fff" }} size="small" />;
    }
    return <Chip label="ผู้พิจารณาอนุมัติ" size="small" />;
  };

  const totalPending = attendanceList.length + leaveList.length;

  return (
    <div>
      {/* 1. Header Summary Cards */}
      <GridContainer>
        {/* การ์ด 1: รวมทั้งหมดที่รออนุมัติ */}
        <GridItem xs={12} sm={6} md={4}>
          <Card>
            <CardHeader color="rose" stats icon>
              <CardIcon color="rose">
                <VerifiedUser />
              </CardIcon>
              <p className={classes.statsCategory}>รวมรายการรอรับรอง / อนุมัติ</p>
              <h3 className={classes.statsTitle}>
                {totalPending}
                <span className={classes.statsUnit}>รายการ</span>
              </h3>
            </CardHeader>
            <CardFooter stats>
              <div style={{ fontSize: "12px", color: "#888" }}>
                <i className="fa fa-clock mr-1 text-danger"></i>
                รายการค้างที่ถึงลำดับการพิจารณาของท่าน
              </div>
            </CardFooter>
          </Card>
        </GridItem>

        {/* การ์ด 2: ขอลงเวลา กจ. 1 / กจ. 4 */}
        <GridItem xs={12} sm={6} md={4}>
          <Card>
            <CardHeader color="info" stats icon>
              <CardIcon color="info">
                <AccessTime />
              </CardIcon>
              <p className={classes.statsCategory}>คำขอลงเวลาปฏิบัติราชการ</p>
              <h3 className={classes.statsTitle}>
                {attendanceList.length}
                <span className={classes.statsUnit}>รายการ</span>
              </h3>
            </CardHeader>
            <CardFooter stats>
              <div style={{ fontSize: "12px", color: "#888" }}>
                <i className="fa fa-user-check mr-1 text-info"></i>
                ในฐานะพยาน / หัวหน้างาน / HR
              </div>
            </CardFooter>
          </Card>
        </GridItem>

        {/* การ์ด 3: ขออนุมัติลา */}
        <GridItem xs={12} sm={6} md={4}>
          <Card>
            <CardHeader color="warning" stats icon>
              <CardIcon color="warning">
                <FlightTakeoff />
              </CardIcon>
              <p className={classes.statsCategory}>คำขออนุมัติลา (ทุกประเภท)</p>
              <h3 className={classes.statsTitle}>
                {leaveList.length}
                <span className={classes.statsUnit}>รายการ</span>
              </h3>
            </CardHeader>
            <CardFooter stats>
              <div style={{ fontSize: "12px", color: "#888" }}>
                <i className="fa fa-calendar-check mr-1 text-warning"></i>
                ใบลาที่ส่งมารอการพิจารณาเห็นชอบ
              </div>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>

      {/* 2. ปฏิทินแสดงรายการรอรับรอง / อนุมัติ (Unified Approvals Calendar) */}
      <GridContainer style={{ marginTop: "10px" }}>
        <GridItem xs={12}>
          <Card className={classes.calendarCard}>
            <CardHeader color="primary">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <h4 className={classes.cardTitleWhite}>
                    <Today style={{ verticalAlign: "middle", marginRight: "8px" }} />
                    ปฏิทินรายการรอรับรองและอนุมัติ (การลา & ขอลงเวลา)
                  </h4>
                  <p className={classes.cardCategoryWhite}>
                    แสดงตารางเวลาของผู้ใต้บังคับบัญชาและเพื่อนร่วมงานที่ส่งคำขอมาถึงท่าน (คลิกแท่งเพื่อเปิดตรวจสอบและอนุมัติทันที)
                  </p>
                </div>
                <div style={{ minWidth: "180px" }}>
                  <TSSelect2
                    options={fiscalYearOptions}
                    value={fiscalYear}
                    onChange={(e) => setFiscalYear(e.value)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {/* คำอธิบายสัญลักษณ์สี (Legend) */}
              <div className={classes.legendContainer}>
                <span style={{ fontWeight: "bold", fontSize: "13px", color: "#333", marginRight: "8px" }}>
                  <i className="fa fa-tags mr-1 text-primary"></i> สัญลักษณ์สี:
                </span>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#43a047" }}></span>
                  <span>ลาพักผ่อน (แถบยาวหลายวัน)</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#e53935" }}></span>
                  <span>ลาป่วย</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#fb8c00" }}></span>
                  <span>ลากิจ</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#0288d1" }}></span>
                  <span>กจ. 1 (ลืมเข้า)</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#8e24aa" }}></span>
                  <span>กจ. 4 (ลืมออก)</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#d81b60" }}></span>
                  <span>กจ. 1/4 (ลืมเข้า-ออก)</span>
                </div>
              </div>

              {/* Component ปฏิทิน */}
              <div style={{ minHeight: "550px" }}>
                <Fullcalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                  initialView="dayGridMonth"
                  headerToolbar={{
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridMonth,listMonth",
                  }}
                  locale="th"
                  events={calendarEvents}
                  eventClick={handleEventClick}
                  height="auto"
                  displayEventTime={false}
                  buttonText={{
                    today: "วันนี้",
                    month: "รายเดือน",
                    list: "รายการ",
                  }}
                />
              </div>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* 3. ตารางรายการรอรับรอง / อนุมัติ แยกตามแท็บ (Approval Tables) */}
      <GridContainer style={{ marginTop: "20px" }}>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="info">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <h4 className={classes.cardTitleWhite}>
                    <Assignment style={{ verticalAlign: "middle", marginRight: "8px" }} />
                    ตารางรายการรอการรับรองและอนุมัติ
                  </h4>
                  <p className={classes.cardCategoryWhite}>
                    เลือกดูตามประเภทคำขอเพื่อพิจารณาอนุมัติ
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {/* Tab Selector */}
              <Paper square style={{ marginBottom: "20px", boxShadow: "none", borderBottom: "1px solid #e0e0e0" }}>
                <Tabs
                  value={tabIndex}
                  indicatorColor="primary"
                  textColor="primary"
                  onChange={(e, val) => setTabIndex(val)}
                >
                  <Tab
                    className={classes.tabCustom}
                    label={
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <VerifiedUser style={{ fontSize: "18px" }} />
                        <span>ทั้งหมด ({totalPending})</span>
                      </div>
                    }
                  />
                  <Tab
                    className={classes.tabCustom}
                    label={
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <AccessTime style={{ fontSize: "18px" }} />
                        <span>ลงเวลาปฏิบัติราชการ ({attendanceList.length})</span>
                      </div>
                    }
                  />
                  <Tab
                    className={classes.tabCustom}
                    label={
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <FlightTakeoff style={{ fontSize: "18px" }} />
                        <span>ขออนุมัติการลา ({leaveList.length})</span>
                      </div>
                    }
                  />
                </Tabs>
              </Paper>

              {/* ========================================================= */}
              {/* ตารางที่ 1: รายการลงเวลาปฏิบัติราชการ */}
              {/* ========================================================= */}
              {(tabIndex === 0 || tabIndex === 1) && (
                <div style={{ marginBottom: tabIndex === 0 ? "35px" : "0px" }}>
                  {tabIndex === 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                      <AccessTime style={{ color: "#00acc1" }} />
                      <h4 style={{ margin: 0, fontWeight: "bold", color: "#333" }}>
                        รายการลงเวลาปฏิบัติราชการ ({attendanceList.length} รายการ)
                      </h4>
                    </div>
                  )}

                  <div className={classes.tableResponsive}>
                    <Table size="small">
                      <TableHead>
                        <TableRow style={{ backgroundColor: "#f9f9f9" }}>
                          <TableCell style={{ fontWeight: "bold", width: "50px" }}>ลำดับ</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>ผู้ขอลงเวลา</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>วันที่ปฏิบัติงาน</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>ประเภท / เวลา</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>เหตุผล</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>บทบาทของท่าน</TableCell>
                          <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>ดำเนินการ</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {attendanceList.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" style={{ color: "#888", padding: "24px" }}>
                              <i className="fa fa-check-circle fa-2x text-success mb-2"></i>
                              <br />
                              ไม่มีรายการขอลงเวลาที่ค้างรอการอนุมัติ
                            </TableCell>
                          </TableRow>
                        ) : (
                          attendanceList.map((row, idx) => (
                            <TableRow key={row.timeAttendanceRequestID || idx} hover>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                  <Avatar
                                    src={`https://e.trangskin.go.th/avatar/${row.userName}.jpg`}
                                    alt={row.userName}
                                    style={{ width: 34, height: 34 }}
                                  />
                                  <div>
                                    <span style={{ fontWeight: "bold", color: "#222" }}>{row.requesterName}</span>
                                    <br />
                                    <small style={{ color: "#777" }}>@{row.userName}</small>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell style={{ fontWeight: "bold" }}>
                                {moment(row.workDate || row.checkInDT || row.checkOutDT).format("D MMM BB")}
                              </TableCell>
                              <TableCell>
                                <span style={{ color: row.categoryMode === 10 ? "#2e7d32" : "#007bff", fontWeight: "bold" }}>
                                  {getAttendanceTypeText(row)}
                                </span>
                                <br />
                                <small style={{ color: "#666" }}>
                                  {row.checkInDT && row.checkOutDT
                                    ? `${moment(row.checkInDT).format("HH:mm")} - ${moment(row.checkOutDT).format("HH:mm")} น.`
                                    : (row.checkInDT ? `เข้า: ${moment(row.checkInDT).format("HH:mm")} ` : (row.checkOutDT ? `ออก: ${moment(row.checkOutDT).format("HH:mm")}` : ""))}
                                </small>
                              </TableCell>
                              <TableCell style={{ maxWidth: "220px", fontSize: "13px" }}>{row.reason}</TableCell>
                              <TableCell>{getRoleBadge(row.approveStep?.stepOrder, row)}</TableCell>
                              <TableCell align="center">
                                <Button
                                  color="success"
                                  size="sm"
                                  onClick={() =>
                                    history.push(`${basePath}/request/${row.timeAttendanceRequestID}`)
                                  }
                                >
                                  <i className="fa fa-pencil-alt mr-1"></i> ตรวจสอบ / อนุมัติ
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* ========================================================= */}
              {/* ตารางที่ 2: รายการขออนุมัติการลา (Leave Requests) */}
              {/* ========================================================= */}
              {(tabIndex === 0 || tabIndex === 2) && (
                <div>
                  {tabIndex === 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                      <FlightTakeoff style={{ color: "#e91e63" }} />
                      <h4 style={{ margin: 0, fontWeight: "bold", color: "#333" }}>
                        รายการขออนุมัติการลา ({leaveList.length} รายการ)
                      </h4>
                    </div>
                  )}

                  <div className={classes.tableResponsive}>
                    <Table size="small">
                      <TableHead>
                        <TableRow style={{ backgroundColor: "#f9f9f9" }}>
                          <TableCell style={{ fontWeight: "bold", width: "50px" }}>ลำดับ</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>ผู้ขอลา</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>ช่วงวันที่ลา</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>ประเภท / รายละเอียด</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>จำนวน</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>สถานะ</TableCell>
                          <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>ดำเนินการ</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {leaveList.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" style={{ color: "#888", padding: "24px" }}>
                              <i className="fa fa-check-circle fa-2x text-success mb-2"></i>
                              <br />
                              ไม่มีรายการขอลาที่ค้างรอการอนุมัติ
                            </TableCell>
                          </TableRow>
                        ) : (
                          leaveList.map((row, idx) => {
                            const reqName = row.userView
                              ? `${row.userView.firstName} ${row.userView.lastName}`
                              : row.fullName || row.userName || "ผู้ขอลา";
                            const leaveId = row.leaveRequestID || row.id;

                            return (
                              <TableRow key={leaveId || idx} hover>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>
                                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <Avatar
                                      src={`https://e.trangskin.go.th/avatar/${row.userName}.jpg`}
                                      alt={row.userName}
                                      style={{ width: 34, height: 34 }}
                                    />
                                    <div>
                                      <span style={{ fontWeight: "bold", color: "#222" }}>{reqName}</span>
                                      <br />
                                      <small style={{ color: "#777" }}>@{row.userName}</small>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>
                                  {moment(row.startDate || row.start).format("D MMM BB")}
                                  {(row.endDate || row.end) && (row.startDate || row.start) !== (row.endDate || row.end) && ` - ${moment(row.endDate || row.end).format("D MMM BB")}`}
                                </TableCell>
                                <TableCell style={{ maxWidth: "220px", fontSize: "13px" }}>
                                  <span style={{ color: "#e91e63", fontWeight: "bold" }}>
                                    {row.leaveTypeName || (row.leaveTypeKey === 1 ? "ลาป่วย" : row.leaveTypeKey === 2 ? "ลากิจ" : row.leaveTypeKey === 3 ? "ลาพักผ่อน" : "การลา")}
                                  </span>
                                  <br />
                                  <small style={{ color: "#666" }}>{row.leaveDesc || row.title || "-"}</small>
                                </TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>
                                  {row.totalDate || 1} วัน
                                </TableCell>
                                <TableCell>
                                  <Chip label="รอการอนุมัติ" style={{ backgroundColor: "#ff9800", color: "#fff", fontWeight: "bold" }} size="small" />
                                </TableCell>
                                <TableCell align="center">
                                  <Button
                                    color="warning"
                                    size="sm"
                                    onClick={() =>
                                      history.push({
                                        pathname: `/leave/id/${leaveId}`,
                                        state: { leaveRequestID: leaveId },
                                      })
                                    }
                                  >
                                    <i className="fa fa-pencil-alt mr-1"></i> ตรวจสอบ / อนุมัติ
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
};

export default MyApprove;
